<script lang="ts" setup>
import ArticleContent from "@/components/article/ArticleContent.vue";
import ArticleContentCollapser from "@/components/article/ArticleContentCollapser.vue";
import type {
  PolitikenAdvancedDanishEnglishArticle,
  PolitikenAdvancedDanishEnglishTranslation
} from "@/types/article-formats/politiken";

const props = defineProps<{
  article: PolitikenAdvancedDanishEnglishArticle;
  isCollapsible: boolean;
}>();

type TranslationConjugation = {
  conjugation: string;
  pronunciation?: string;
};

const getTranslationConjugations = (
  translation: PolitikenAdvancedDanishEnglishTranslation
): TranslationConjugation[] =>
  [
    { conjugation: translation.conjugation1, pronunciation: translation.pronunciationConjugation1 },
    { conjugation: translation.conjugation2, pronunciation: translation.pronunciationConjugation2 },
    { conjugation: translation.conjugation3, pronunciation: translation.pronunciationConjugation3 },
    { conjugation: translation.conjugation4, pronunciation: translation.pronunciationConjugation4 }
  ].filter((conjugation) => conjugation.conjugation !== "");

const getTranslationEquivalentConjugations = (
  translation: PolitikenAdvancedDanishEnglishTranslation
): TranslationConjugation[] =>
  [
    { conjugation: translation.conjugation1Equivalent3 },
    { conjugation: translation.conjugation2Equivalent3 },
    { conjugation: translation.conjugation3Equivalent3 },
    { conjugation: translation.conjugation4Equivalent3 }
  ].filter((conjugation) => conjugation.conjugation !== "");
</script>

<template>
  <!-- eslint-disable vue/no-v-html -->
  <ArticleContent>
    <div class="article-content__header article-header">
      <div class="article-header__content">
        <h5 class="article-header__word" v-html="props.article.word.lemma"></h5>
        <sup v-if="props.article.word.hnr && props.article.word.hnr !== '0'" class="pol-header-sup">
          {{ props.article.word.hnr }}
        </sup>

        <h5
          v-if="props.article.word.lemmaAlternative"
          class="article-header__word pl-1"
          v-html="props.article.word.lemmaAlternative"
        ></h5>

        <span
          v-if="props.article.word.lemmaWordClass"
          class="article-header__element article-header__element--light article-header__element--italic"
        >
          {{ props.article.word.lemmaWordClass }}
        </span>

        <h5
          v-if="props.article.word.lemmaVariation"
          class="article-header__word"
          v-html="props.article.word.lemmaVariation"
        ></h5>

        <span
          v-if="props.article.word.lemmaConjugation"
          class="article-header__inflections article-header__inflections--prominent article-header__inflections--unchained"
        >
          {{ props.article.word.lemmaConjugation }}
        </span>
      </div>
    </div>

    <ArticleContentCollapser :is-collapsible="props.isCollapsible">
      <div class="article-content__body-container article-content__body-container--reading-lines">
        <div class="indentation-container">
          <div
            v-for="(content, contentIndex) in props.article.content"
            :key="contentIndex"
            class="indentation-container"
          >
            <template v-if="content.translation?.length === 0">
              <span
                v-if="content.conjugations"
                class="article-list-bullet article-list-bullet--primary article-list-bullet--prominent"
              >
                {{ content.conjugations }}
              </span>

              <span
                v-if="content.danishPhrase"
                class="article-content__text article-content__text--secondary article-content__text--prominent"
                v-html="content.danishPhrase"
              ></span>
            </template>

            <div
              v-for="(translation, translationIndex) in content.translation ?? []"
              :key="translationIndex"
            >
              <template v-if="translationIndex === 0">
                <span
                  v-if="content.conjugations"
                  class="article-list-bullet article-list-bullet--primary article-list-bullet--prominent"
                >
                  {{ content.conjugations }}
                </span>

                <span
                  v-if="content.danishPhrase"
                  class="article-content__text article-content__text--secondary article-content__text--prominent"
                  v-html="content.danishPhrase"
                ></span>
              </template>

              <span
                v-if="translation.ko1Equivalent"
                class="article-content__text article-content__text--secondary pl-1"
              >
                {{ translation.ko1Equivalent }}
              </span>

              <span
                v-if="translation.equivalent"
                class="article-content__text article-content__text--primary article-content__text--prominent pl-1"
                v-html="translation.equivalent"
              ></span>

              <span
                v-if="translation.ko2Equivalent"
                class="article-content__text article-content__text--comment pl-1"
              >
                {{ translation.ko2Equivalent }}
              </span>

              <span
                v-if="translation.koEquivalent2"
                class="article-content__text article-content__text--comment"
              >
                {{ translation.koEquivalent2 }}
              </span>

              <span
                v-if="translation.equivalent2"
                class="article-content__text article-content__text--primary article-content__text--prominent"
                :class="{ 'pl-1': translation.equivalent2.charAt(0) !== ',' }"
              >
                {{ translation.equivalent2 }}
              </span>

              <span
                v-if="translation.equivalent22"
                class="article-content__text article-content__text--primary article-content__text--prominent"
              >
                {{ translation.equivalent22 }}
              </span>

              <span
                v-if="translation.equivalentWordClass"
                class="article-content__text article-content__text--light article-content__text--italic pl-2"
              >
                {{ translation.equivalentWordClass }}
              </span>

              <span
                v-if="translation.pronunciation"
                class="article-content__text article-content__text--muted pl-2 pronunciation"
                v-html="translation.pronunciation"
              ></span>

              <span v-if="getTranslationConjugations(translation).length > 0" class="pl-2"
                >(<span
                  v-for="(conjugation, conjugationIndex) in getTranslationConjugations(translation)"
                  :key="conjugationIndex"
                  ><span
                    class="article-content__text article-content__text--prominent"
                    :class="{ 'ml-1': conjugationIndex !== 0 }"
                    v-html="conjugation.conjugation"
                  ></span
                  ><span
                    v-if="conjugation.pronunciation"
                    class="article-content__text article-content__text--muted pl-1 pronunciation"
                    v-html="conjugation.pronunciation"
                  ></span></span
                >)</span
              >

              <span
                v-if="translation.koEquivalent3"
                class="article-content__text article-content__text--comment"
              >
                {{ translation.koEquivalent3 }}
              </span>

              <span
                v-if="translation.equivalent3"
                class="article-content__text article-content__text--primary article-content__text--prominent"
                :class="{ 'pl-2': translation.equivalent3.charAt(0) !== ';' }"
              >
                {{ translation.equivalent3 }}
              </span>

              <span
                v-if="translation.pronunciationEquivalent3"
                class="article-content__text article-content__text--muted pl-2 pronunciation"
                v-html="translation.pronunciationEquivalent3"
              ></span>

              <span v-if="getTranslationEquivalentConjugations(translation).length > 0" class="pl-2"
                >(<span
                  v-for="(conjugation, conjugationIndex) in getTranslationEquivalentConjugations(
                    translation
                  )"
                  :key="conjugationIndex"
                  ><span
                    class="article-content__text article-content__text--prominent"
                    :class="{ 'ml-1': conjugationIndex !== 0 }"
                    v-html="conjugation.conjugation"
                  ></span></span
                >)</span
              >

              <template
                v-for="(definition, definitionIndex) in translation.definition ?? []"
                :key="definitionIndex"
              >
                <div v-if="definition.definition" class="article-content__politiken-grid">
                  <div class="article-content__text article-content__politiken-grid-label">
                    Definition:
                  </div>
                  <div
                    class="article-content__politiken-grid-content article-content__text article-content__text--muted"
                  >
                    <span
                      v-if="definition.glossary"
                      class="pr-1"
                      v-html="definition.glossary"
                    ></span>
                    <span v-if="definition.usage" class="pr-1" v-html="definition.usage"></span>
                    <span v-html="definition.definition"></span>
                  </div>
                </div>
                <div
                  v-else-if="definition.glossary || definition.usage"
                  class="article-content__text article-content__text--muted"
                >
                  <span v-if="definition.glossary" class="pr-1" v-html="definition.glossary"></span>
                  <span v-if="definition.usage" v-html="definition.usage"></span>
                </div>

                <div
                  v-if="definition.grammar || definition.wordClass"
                  class="article-content__politiken-grid"
                >
                  <div class="article-content__text article-content__politiken-grid-label">
                    Grammatik:
                  </div>
                  <div
                    class="article-content__politiken-grid-content article-content__text article-content__text--light"
                  >
                    <span v-if="definition.wordClass">{{ definition.wordClass }}</span>
                    <span v-if="definition.grammar" v-html="definition.grammar"></span>
                  </div>
                </div>

                <div v-if="definition.nb" class="article-content__politiken-grid">
                  <div class="article-content__text article-content__politiken-grid-label">NB:</div>
                  <div
                    class="article-content__politiken-grid-content article-content__text article-content__text--muted"
                    v-html="definition.nb"
                  ></div>
                </div>

                <div v-if="definition.synonym" class="article-content__politiken-grid">
                  <div class="article-content__text article-content__politiken-grid-label">
                    Synonym:
                  </div>
                  <div
                    class="article-content__politiken-grid-content article-content__text article-content__text--muted"
                  >
                    {{ definition.synonym }}
                  </div>
                </div>

                <div v-if="definition.antonym" class="article-content__politiken-grid">
                  <div class="article-content__text article-content__politiken-grid-label">
                    Antonym:
                  </div>
                  <div
                    class="article-content__politiken-grid-content article-content__text article-content__text--muted"
                    v-html="definition.antonym"
                  ></div>
                </div>

                <div
                  v-if="(definition.example ?? []).length > 0"
                  class="article-content__politiken-grid"
                >
                  <div class="article-content__text article-content__politiken-grid-label">
                    Eksempel:
                  </div>
                  <div class="article-content__politiken-grid-content">
                    <div v-for="(example, exampleIndex) in definition.example" :key="exampleIndex">
                      <span
                        v-if="example.grammar"
                        class="article-content__text article-content__text--light pr-2"
                        v-html="example.grammar"
                      ></span>
                      <span
                        v-if="example.example"
                        class="article-content__text article-content__text--secondary"
                        v-html="example.example"
                      ></span>
                    </div>
                  </div>
                </div>

                <div
                  v-if="definitionIndex < (translation.definition ?? []).length - 1"
                  class="article-content__spacer"
                />
              </template>

              <div class="article-content__spacer" />
            </div>
          </div>
        </div>
      </div>
    </ArticleContentCollapser>
  </ArticleContent>
</template>

<style lang="less" scoped>
// Hack to ensure superscript HTML elements doesn't shift the line height
.pronunciation {
  line-height: 0;
}
</style>
